import React from 'react';

import { captureClientError } from '../sentry.js';

/**
 * Top-level React error boundary. Any uncaught render error in a descendant
 * bubbles here and we show a friendly fallback instead of a white screen.
 *
 * Wire once at App.jsx root; per-module ErrorBoundaries can be nested later
 * to localize failures.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Forwards to Sentry when VITE_SENTRY_DSN is set; a no-op otherwise, which
    // is why the console.error stays — it's the only signal in dev.
    captureClientError(error, info);
     
    console.error('[ErrorBoundary] caught:', error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex min-h-screen items-center justify-center bg-sky-100 p-8">
        <div className="max-w-md rounded-xl border border-sky-300 bg-white p-8 shadow-sm">
          <h1 className="mb-2 text-xl font-semibold text-ink-900">Something went wrong.</h1>
          <p className="mb-6 text-sm text-ink-500">
            An unexpected error occurred. Refreshing will usually fix it. If not, contact an admin.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-green-400 px-6 py-3 text-sm font-semibold uppercase text-white transition-all hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-green-400 focus:ring-offset-2"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
